//!#system	

import apiService from '@/apiService';
import fieldInfoMap from '@/fieldInfoMap';

const objectCreatedDefault = {
	show: false,
	multiline: false,
	text: '',
	linkTo: {},
	timeout: 6000
};

const objectUpdatedDefault = {
	show: false,
	multiline: false,
	text: '',
	linkTo: {},
	timeout: 6000
};

const objectErrorDefault = {
	show: false,
	multiline: false,
	text: '',
	linkTo: {},
	timeout: 6000
};

// initial state
const state = {
	loaded: false,
	apiUrl: '/api',
	appName: 'Shine',
	appHome: {
		heroLine: 'Shine',
		secondLine: 'Login and get started!'
	},
	engineVersion: '',
	appVersion: '',
	subscription: {},
	mainDrawer: null,
	mains: [],
	settings: [],
	admin: [],
	browser: [],
	errorState: false,
	errorMessage: '',
	objectCreated: {...objectCreatedDefault},
	objectUpdated: {...objectUpdatedDefault},
	objectError: {...objectErrorDefault},
	fieldInfos: fieldInfoMap,
	customFieldSupplementsFetched: false,
	searchObjects: {
		main: '',
		panels: [],
		current: ''
	},
	dashboard: {
		dashboardKey: 'DEFAULT'
	},
	contextLinks: {}
};

document.title = state.appName;

// getters
const getters = {
	isLoaded: () => {
		return state.loaded;
	}
};

const getContext = function(context, done) {
	apiService.call({
		path: '/_sys/internals/context/' + encodeURIComponent(context)
	}, (err, data) => {
		done(err, data);
	});
};

const getSearchObjects = function(done) {
	apiService.call({
		path: '/_sys/internals/app-info/search-objects'
	}, (err, data) => {
		done(err, data);
	});
};

const getVersions = function(done) {
	apiService.call({
		path: '/_sys/internals/app-info/versions'
	}, (err, data) => {
		done(err, data);
	});
};

const getCustomFieldInfoSupplement = function(done) {
	apiService.call({
		path: '/_sys/internals/app-info/custom-field-info'
	}, (err, data) => {
		done(err, data);
	});
};

const getFieldInfoKey = function(routeInfo) {
	if (routeInfo.name && routeInfo.namespace) return (routeInfo.namespace + '.' + routeInfo.name);
	if (routeInfo.pluralName && routeInfo.namespace) {
		const bucket = routeInfo.namespace + '/' + routeInfo.pluralName;
		if (state.fieldInfos._buckets[bucket]) return state.fieldInfos._buckets[bucket];
	}
	return '';
};

// actions
const actions = {
	setLoaded({ commit }) {
		commit('setStateLoaded');
	},
	toggleDrawer({ commit }) {
		let newDrawer = false;
		if (state.mainDrawer === null) {
			newDrawer = true;
		} else {
			newDrawer = !state.mainDrawer;
		}
		commit('setDrawer', { mainDrawer: newDrawer })
	},
	setDashboard({ commit }, { dashboard }) {
		commit('setDashboardKey', { dashboard })
	},
	getMainContext({ commit }) {
		getContext('Main', (err, data) => {
			commit('setMains', data);
		});
	},
	getSettingContext({ commit }) {
		getContext('Setting', (err, data) => {
			commit('setSettings', data);
		});
	},
	getAdminContext({ commit }) {
		getContext('Admin', (err, data) => {
			commit('setAdmin', data);
		});
	},
	getBrowserContext({ commit }) {
		getContext('Browser', (err, data) => {
			commit('setBrowser', data);
		});
	},
	getSearchContext({ commit }) {
		getSearchObjects((err, data) => {
			commit('setSearches', data);
		});
	},
	openSearchPanel({ commit }, name) {
		commit('setOpenSearchPanel', { name });
	},
	closeSearchPanel({ commit }, name) {
		commit('setCloseSearchPanel', { name });
	},
	getVersionData({ commit }) {
		getVersions((err, data) => {
			commit('setVersions', data);
		});
	},
	applyErrorState({ commit }, errorState) {
		commit('setErrorState', errorState);
	},
	applyObjectCreated({ commit }, objectCreated) {
		const objectCreatedObj = {...objectCreatedDefault, ...objectCreated};
		commit('setObjectCreated', objectCreatedObj);
	},
	applyObjectError({ commit }, objectError) {
		const objectErrorObj = {...objectErrorDefault, ...objectError};
		commit('setObjectError', objectErrorObj);
	},
	applyObjectUpdated({ commit }, objectUpdated) {
		const objectUpdatedObj = {...objectUpdatedDefault, ...objectUpdated};
		commit('setObjectUpdated', objectUpdatedObj);
	},
	clearObjectCreated({ commit }) {
		commit('setObjectCreated', objectCreatedDefault);
	},
	clearObjectUpdated({ commit }) {
		commit('setObjectUpdated', objectUpdatedDefault);
	},
	clearObjectError({ commit }) {
		commit('setObjectError', objectErrorDefault);
	},
	fieldInfo({ commit }, { routeInfo, callback }) {
		let name = routeInfo.name
		const key = getFieldInfoKey(routeInfo);
		if (!state.customFieldSupplementsFetched && state.fieldInfos[key]) {
			state.customFieldSupplementsFetched = true;
			return apiService.call({
				path: '/_sys/internals/app-info/custom-field-info'
			}, (err, data) => {
				if (!err) commit('mergeCustomFieldInfo', { data });
				if (state.fieldInfos[key]) return callback(null, state.fieldInfos[key]);
			});
		}
		if (state.fieldInfos[key]) return callback(null, state.fieldInfos[key]);
		apiService.fields(routeInfo, (err, response, meta) => {
			if (err) {
				this.dispatch('app/applyObjectError', {
					show: true,
					text: meta.validation ? meta.validation.messages.join('\n') : err
				});
				return callback(err);
			}
			commit('setFieldInfo', {
				key,
				response
			});
			return callback(null, state.fieldInfos[key]);
		});
	},
	customFields({ commit }, { routeInfo, callback }) {
		let name = routeInfo.name
		const key = getFieldInfoKey(routeInfo);
		const obj = state.fieldInfos[key];
		return callback(null, obj.fields.filter(f => f.isCustom === true));
	},
	fetchCustomFieldInfo({ commit }, { callback }) {
		if (!state.customFieldSupplementsFetched) {
			state.customFieldSupplementsFetched = true;
			return apiService.call({
				path: '/_sys/internals/app-info/custom-field-info'
			}, (err, data) => {
				if (!err) commit('mergeCustomFieldInfo', { data });
				callback(err);
			});
		}
	},
	buckets({ commit }, { options, callback }) {
		if (!options) {
			options = {};
		}
		const outArrs = {
			mains: [],
			others: [],
			builtins: []
		};
		const fiKeys = Object.values(state.fieldInfos._buckets);
		fiKeys.forEach(k => {
			if (options.createable && state.fieldInfos[k] && !state.fieldInfos[k].createable) return;
			if (options.omitNamespaces && state.fieldInfos[k] && options.omitNamespaces.includes(state.fieldInfos[k].namespace)) {
				if (options.keepBuckets && options.keepBuckets.includes(state.fieldInfos[k].namespace + '/' + state.fieldInfos[k].pluralName)) {
					// let it continue
				} else {
					return;
				}
			}
			if (state.fieldInfos[k].contextLevel === 'Main') {
				outArrs.mains.push(state.fieldInfos[k].namespace + '/' + state.fieldInfos[k].pluralName);
			} else if (state.fieldInfos[k].isBuiltin) {
				outArrs.builtins.push(state.fieldInfos[k].namespace + '/' + state.fieldInfos[k].pluralName);
			} else {
				outArrs.others.push(state.fieldInfos[k].namespace + '/' + state.fieldInfos[k].pluralName);
			}
		});
		outArrs.mains.sort();
		outArrs.builtins.sort();
		outArrs.others.sort();
		return callback(null, outArrs.mains.concat(outArrs.others, outArrs.builtins));
	},
	getAllCustomFieldSupplements({ commit }) {
		getCustomFieldInfoSupplement((err, data) => {
			if (!err) commit('mergeCustomFieldInfo', { data });
		});
	},
	cacheContextLink({ commit }, { key, link }) {
		commit('storeContextLink', { key, link });
	}
};

// mutations
const mutations = {
	setStateLoaded() {
		state.loaded = true;
	}, 	
	setDrawer(state, payload) {
		state.mainDrawer = payload.mainDrawer;
	},
	setDashboardKey(state, payload) {
		state.dashboard.dashboardKey = payload.dashboard.dashboardKey;
	},
	setMains(state, payload) {
		state.mains = payload;
	},
	setSettings(state, payload) {
		state.settings = payload;
	},
	setAdmin(state, payload) {
		state.admin = payload;
	},
	setBrowser(state, payload) {
		state.browser = payload;
	},
	setVersions(state, payload) {
		state.engineVersion = payload.engineVersion;
		state.appVersion = payload.appVersion;
	},
	setSearches(state, payload) {
		state.searchObjects = { ...payload };
	},
	setOpenSearchPanel(state, payload) {
		state.searchObjects.panels.forEach(panel => {
			if (payload.name === panel.namespace + '.' + panel.name) {
				panel.open = true;
				state.searchObjects.current = panel.namespace + '.' + panel.name;
			}
		});
	},
	setCloseSearchPanel(state, payload) {
		state.searchObjects.panels.forEach(panel => {
			if (payload.name === panel.namespace + '.' + panel.name) {
				panel.open = false;
				state.searchObjects.current = panel.namespace + '.' + panel.name;
			}
		});
	},
	setErrorState(state, payload) {
		state.errorState = !!payload.errorState;
		state.errorMessage = payload.errorMessage || 'General Error';
	},
	setObjectCreated(state, payload) {
		state.objectCreated = payload;
	},
	setObjectUpdated(state, payload) {
		state.objectUpdated = payload;
	},
	setObjectError(state, payload) {
		state.objectError = payload;
	},
	setFieldInfo(state, payload) {
		state.fieldInfos[payload.key] = payload.response;
	},
	mergeCustomFieldInfo(state, payload) {
		const customFieldsObject = payload.data;
		const supplementKeysLoaded = Object.keys(customFieldsObject);
		supplementKeysLoaded.forEach(key => {
			if (!state.fieldInfos[key]) return;
			const customFields = customFieldsObject[key];
			customFields.fields.forEach(field => {
				state.fieldInfos[key].fields.push(field);
			});
		});
	},
	storeContextLink(state, payload) {
		state.contextLinks[payload.key] = payload.link;
	}
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
};